
import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, Key, Settings, Shield, RefreshCw, Activity } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { motion } from 'framer-motion';
import { fadeInScale, glowPulse } from '@/lib/animations';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { addDays, format, isAfter } from 'date-fns';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export interface YouTubeQuotaInfoProps {
  className?: string;
  size?: 'default' | 'small' | 'large';
}

const YouTubeQuotaInfo: React.FC<YouTubeQuotaInfoProps> = ({ className, size = 'default' }) => {
  const [quotaUsed, setQuotaUsed] = useState<number>(0); 
  const [resetTime, setResetTime] = useState<Date | null>(null);
  const [isExceeded, setIsExceeded] = useState<boolean>(false);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isUsingUserKey, setIsUsingUserKey] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [apiKeyAvailable, setApiKeyAvailable] = useState<boolean>(true);
  const [queriesUsed, setQueriesUsed] = useState<number>(0);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const { user } = useAuth();

  const fetchQuotaData = async () => {
    setIsLoading(true);
    try {
      // Vérifier si une clé API utilisateur est disponible
      const userKey = await api.getUserApiKey();
      setIsUsingUserKey(!!userKey);
      setApiKeyAvailable(!!userKey);
      
      if (!userKey) {
        setIsLoading(false);
        return;
      }
      
      // Obtenir les données réelles de quota via l'API
      const quotaData = await api.getYouTubeQuotaUsage();
      console.log('Fetched quota data:', quotaData);
      
      if (!quotaData) {
        console.error("Impossible de récupérer les données de quota YouTube");
        toast.error("Erreur lors de la récupération des données de quota YouTube");
        setIsLoading(false);
        return;
      }
      
      // Stocker les données de quota dans le localStorage pour garantir la cohérence entre les actualisations
      const storedData = {
        quotaPercentage: quotaData.quotaPercentage,
        queriesUsed: quotaData.queriesUsed,
        isExceeded: quotaData.isExceeded,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('youtube_quota_data', JSON.stringify(storedData));
      
      console.log('Données de quota récupérées:', quotaData);
      
      // Mettre à jour l'état avec les données réelles
      setQuotaUsed(quotaData.quotaPercentage);
      setQueriesUsed(quotaData.queriesUsed);
      setIsExceeded(quotaData.isExceeded);
      setLastRefreshTime(new Date());
      
    } catch (error) {
      console.error('Erreur lors de la récupération des données de quota:', error);
      toast.error("Erreur lors de la récupération des données de quota");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadStoredQuotaData = () => {
      const storedDataString = localStorage.getItem('youtube_quota_data');
      if (storedDataString) {
        try {
          const storedData = JSON.parse(storedDataString);
          const storedTimestamp = new Date(storedData.timestamp);
          const now = new Date();
          
          // Vérifier si les données stockées sont encore valides (moins de 1 heure)
          const diffMs = now.getTime() - storedTimestamp.getTime();
          const diffHours = diffMs / (1000 * 60 * 60);
          
          if (diffHours < 1) {
            // Utiliser les données stockées
            setQuotaUsed(storedData.quotaPercentage);
            setQueriesUsed(storedData.queriesUsed);
            setIsExceeded(storedData.isExceeded);
            setLastRefreshTime(storedTimestamp);
            setIsLoading(false);
            return true;
          }
        } catch (error) {
          console.error('Erreur lors de la lecture des données stockées:', error);
        }
      }
      return false;
    };
    
    const calculateResetTime = () => {
      const pacificTimeZone = 'America/Los_Angeles';
      const now = new Date();
      
      const pacificDate = toZonedTime(now, pacificTimeZone);
      
      let resetDate = toZonedTime(now, pacificTimeZone);
      resetDate.setHours(0, 0, 0, 0);
      
      if (isAfter(pacificDate, resetDate)) {
        resetDate = addDays(resetDate, 1);
      }
      
      return resetDate;
    };
    
    const resetTime = calculateResetTime();
    setResetTime(resetTime);
    
    // D'abord essayer de charger les données stockées
    const hasStoredData = loadStoredQuotaData();
    
    // Si aucune donnée stockée valide, effectuer une nouvelle requête
    if (!hasStoredData) {
      fetchQuotaData();
    }
    
    const updateTimeRemaining = () => {
      if (resetTime) {
        const now = new Date();
        const diffMs = resetTime.getTime() - now.getTime();
        
        if (diffMs <= 0) {
          const newResetTime = calculateResetTime();
          setResetTime(newResetTime);
          return;
        }
        
        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        
        setTimeRemaining(`${diffHrs}h ${diffMins}m`);
      }
    };
    
    updateTimeRemaining();
    const intervalId = setInterval(updateTimeRemaining, 60000);
    
    return () => clearInterval(intervalId);
  }, []);
  
  const getProgressClasses = () => {
    if (quotaUsed >= 90) {
      return 'bg-red-100 dark:bg-red-950';
    } else if (quotaUsed >= 70) {
      return 'bg-amber-100 dark:bg-amber-950';
    } else {
      return 'bg-green-100 dark:bg-green-950';
    }
  };
  
  const getIndicatorClasses = () => {
    if (quotaUsed >= 90) {
      return 'bg-red-500';
    } else if (quotaUsed >= 70) {
      return 'bg-amber-500';
    } else {
      return 'bg-green-500';
    }
  };
  
  const refreshQuotaStatus = async () => {
    await fetchQuotaData();
  };
  
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'p-4';
      case 'large':
        return 'p-6 md:p-10';
      case 'default':
      default:
        return 'p-6 md:p-8';
    }
  };
  
  // Format queries count with commas
  const formattedQueriesUsed = queriesUsed.toLocaleString();
  
  // Format last refresh time
  const formatLastRefreshTime = () => {
    if (!lastRefreshTime) return '';
    
    const now = new Date();
    const diffMs = now.getTime() - lastRefreshTime.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'à l\'instant';
    if (diffMins === 1) return 'il y a 1 minute';
    if (diffMins < 60) return `il y a ${diffMins} minutes`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return 'il y a 1 heure';
    return `il y a ${diffHours} heures`;
  };
  
  if (!apiKeyAvailable) {
    return (
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeInScale}
        className={className}
      >
        <Card className="shadow-md border border-gray-200 dark:border-gray-800 overflow-hidden backdrop-blur-sm bg-white/95 dark:bg-gray-800/95">
          <div className="absolute top-0 left-0 w-full h-1 bg-amber-500"></div>
          <CardContent className={getSizeClasses()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl md:text-2xl font-semibold flex items-center gap-2">
                  <Key className="w-6 h-6 text-primary" />
                  Clé API YouTube requise
                </h3>
                <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">
                  Veuillez ajouter votre clé API YouTube personnelle pour continuer
                </p>
              </div>
              <motion.div 
                className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center text-amber-600"
                initial="initial"
                animate="animate"
                variants={glowPulse}
              >
                <AlertTriangle className="w-6 h-6" />
              </motion.div>
            </div>
            
            <div className="mt-6 text-sm md:text-base text-amber-500 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-md border border-amber-100 dark:border-amber-800 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="mb-2">Pour utiliser cette application, vous devez ajouter votre propre clé API YouTube dans les paramètres.</p>
                
                {user ? (
                  <Button
                    variant="default"
                    size="sm"
                    className="mt-2"
                    asChild
                  >
                    <Link to="/settings">
                      <Settings className="w-4 h-4 mr-2" />
                      Ajouter ma clé API YouTube
                    </Link>
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    className="mt-2"
                    asChild
                  >
                    <Link to="/auth">
                      <Key className="w-4 h-4 mr-2" />
                      Se connecter pour ajouter ma clé API
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }
  
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={fadeInScale}
      className={className}
    >
      <Card className="shadow-md border border-gray-200 dark:border-gray-800 overflow-hidden backdrop-blur-sm bg-white/95 dark:bg-gray-800/95">
        <div className={`absolute top-0 left-0 w-full h-1 ${
          isExceeded ? 'bg-red-500' : quotaUsed >= 70 ? 'bg-amber-500' : 'bg-green-500'
        }`}></div>
        <CardContent className={getSizeClasses()}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl md:text-2xl font-semibold flex items-center gap-2">
                <Shield className="w-6 h-6 text-primary" />
                YouTube API Quota
              </h3>
              <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">
                {isLoading 
                  ? "Vérification du statut..."
                  : isExceeded 
                    ? "Quota dépassé pour aujourd'hui" 
                    : "Utilisation réelle du quota (Google Cloud)"}
              </p>
              {isUsingUserKey && (
                <span className="inline-flex mt-1 items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                  Clé API personnelle active
                </span>
              )}
            </div>
            {isExceeded && (
              <motion.div 
                className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center text-red-600"
                initial="initial"
                animate="animate"
                variants={glowPulse}
              >
                <AlertTriangle className="w-6 h-6" />
              </motion.div>
            )}
          </div>
          
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Utilisation</span>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 px-2" 
                    onClick={refreshQuotaStatus}
                    disabled={isLoading}
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                    <span className="sr-only">Actualiser</span>
                  </Button>
                  <span className={`text-sm font-medium ${
                    quotaUsed >= 90 ? 'text-red-500' : quotaUsed >= 70 ? 'text-amber-500' : 'text-green-500'
                  }`}>
                    {quotaUsed}%
                  </span>
                </div>
              </div>
              <Progress 
                value={quotaUsed} 
                className={getProgressClasses()}
                style={{
                  height: '10px',
                  borderRadius: '5px'
                }}
                indicatorClassName={getIndicatorClasses()}
              />
              {lastRefreshTime && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Dernière actualisation: {formatLastRefreshTime()}
                </p>
              )}
            </div>
            
            {/* Display queries used section */}
            <div className="flex items-center gap-2 py-2 px-3 bg-gray-50 dark:bg-gray-700/30 rounded-md">
              <Activity className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-sm font-medium">Requêtes API utilisées aujourd'hui</p>
                <p className="text-md font-semibold text-blue-600 dark:text-blue-400">
                  {formattedQueriesUsed} <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">sur 10,000 (quota quotidien)</span>
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-4 mt-6">
              <div className="flex items-center text-gray-500 dark:text-gray-400">
                <Clock className="w-5 h-5 mr-2" />
                <span className="text-sm md:text-base">Réinitialisation dans:</span>
              </div>
              <motion.span 
                className="font-medium bg-gray-100 dark:bg-gray-700 py-1 px-3 rounded-md text-sm md:text-base"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                {timeRemaining}
              </motion.span>
            </div>
            
            {isExceeded && (
              <motion.div 
                className="mt-6"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="text-sm md:text-base text-red-500 p-4 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-100 dark:border-red-800 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="mb-2">Votre clé API YouTube a atteint sa limite quotidienne. Les données seront à nouveau disponibles après la réinitialisation.</p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default YouTubeQuotaInfo;
